import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@opendoor/atoms/button';
import { globalObservability } from '@opendoor/observability/slim';
import type { MarketplaceBrowseMapMarkers } from 'components/marketplace/browse/marketplaceBrowse';
import type { LikedPropertyIds } from 'components/marketplace/types';
import { useIsomorphicLayoutEffect } from 'components/shared/hooks/useIsomorphicLayoutEffect';
import { useMediaQuery } from 'components/shared/hooks/useMediaQuery';
import { useObservability } from 'helpers/observability';
import type { FilterSpecification, MapMouseEvent, SymbolLayerSpecification } from 'mapbox-gl';
import Router from 'next/router';
import { Layer, Source, useMap } from 'react-map-gl/mapbox';

import { buildMarkerAriaLabel, MapMarkerPopup } from './MapMarker';
import {
  getRegisteredMapMarkerImageIds,
  refreshMapMarkerImages,
  registerMapMarkerImage,
  registerMapMarkerImages,
} from './mapMarkerImage';
import {
  buildMapMarkerFeatureCollection,
  MARKETPLACE_MARKER_SOURCE_ID,
  marketplaceHighlightedMarkerLayer,
  marketplaceMarkerAddressIdAtPoint,
  marketplaceMarkerLayer,
} from './mapMarkerLayer';
import type { MapMarkerProperty } from './types';

const HOVER_INTENT_MS = 120;

const markerImpressionsFired = new Set<string>();
let routerListenerAttached = false;

function ensureRouterListener() {
  if (routerListenerAttached || typeof window === 'undefined') {
    return;
  }
  routerListenerAttached = true;
  Router.events.on('routeChangeStart', () => {
    markerImpressionsFired.clear();
  });
}

function trackMarkerImpression(addressToken: string) {
  if (!addressToken || markerImpressionsFired.has(addressToken)) {
    return;
  }
  markerImpressionsFired.add(addressToken);
  try {
    ensureRouterListener();
    globalObservability.getDatadogRumClient?.()?.addAction?.('marketplace.mapMarkerHover', {
      addressToken,
    });
  } catch {
    // Analytics availability must not affect marketplace interactions.
  }
}

interface MarketplaceMapMarkersProps {
  markers?: MarketplaceBrowseMapMarkers | null;
  likedPropertyIds?: LikedPropertyIds;
  selectedAddressId?: string;
  loadingAddressId?: string | null;
  onListingClick: (addressId: string) => void;
}

type AccessibleMapMarkerProps = {
  isDesktop: boolean;
  isFocused: boolean;
  onBlur: () => void;
  onFocus: (addressId: string) => void;
  onListingClick: (addressId: string) => void;
  popupId: string | undefined;
  property: MapMarkerProperty;
};

const AccessibleMapMarker = memo(
  ({
    isDesktop,
    isFocused,
    onBlur,
    onFocus,
    onListingClick,
    popupId,
    property,
  }: AccessibleMapMarkerProps) => {
    const handleFocus = useCallback(() => {
      onFocus(property.addressId);
    }, [onFocus, property.addressId]);
    const handleClick = useCallback(() => {
      onListingClick(property.addressId);
    }, [onListingClick, property.addressId]);

    return (
      <Button
        analyticsName="property-marker-click"
        aria-label={buildMarkerAriaLabel(property)}
        aria-expanded={isFocused && isDesktop}
        aria-describedby={isFocused && isDesktop ? popupId : undefined}
        onFocus={handleFocus}
        onBlur={onBlur}
        onClick={handleClick}
        className="sr-only !h-px !w-px"
      />
    );
  },
);

AccessibleMapMarker.displayName = 'AccessibleMapMarker';

export const MarketplaceMapMarkers = memo(
  ({
    markers,
    likedPropertyIds,
    selectedAddressId,
    loadingAddressId,
    onListingClick,
  }: MarketplaceMapMarkersProps) => {
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const { trackJourneyEvent } = useObservability();
    const { current: mapRef } = useMap();
    const map = mapRef?.getMap();
    const [hoveredAddressId, setHoveredAddressId] = useState<string | null>(null);
    const [focusedAddressId, setFocusedAddressId] = useState<string | null>(null);
    const hoverCandidateAddressIdRef = useRef<string | null>(null);
    const hoverTimerRef = useRef<number | null>(null);

    const featureCollection = useMemo(
      () =>
        buildMapMarkerFeatureCollection({
          loadingAddressId,
          likedPropertyIds,
          markers,
          selectedAddressId,
        }),
      [likedPropertyIds, loadingAddressId, markers, selectedAddressId],
    );

    const propertiesByAddressId = useMemo(() => {
      const properties = new Map<string, MapMarkerProperty>();
      for (const marker of markers ?? []) {
        const { addressId, location, ...listing } = marker;
        properties.set(addressId, {
          ...listing,
          addressId,
          latitude: location.latitude,
          longitude: location.longitude,
          isLiked: likedPropertyIds?.has(addressId) ?? false,
        });
      }
      return properties;
    }, [likedPropertyIds, markers]);

    const cancelHoverTimer = useCallback(() => {
      if (hoverTimerRef.current != null) {
        window.clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    }, []);

    const clearHover = useCallback(() => {
      cancelHoverTimer();
      hoverCandidateAddressIdRef.current = null;
      setHoveredAddressId(null);
    }, [cancelHoverTimer]);

    const openListing = useCallback(
      (addressId: string) => {
        clearHover();
        setFocusedAddressId(null);
        onListingClick(addressId);
      },
      [clearHover, onListingClick],
    );

    const openCanvasListing = useCallback(
      (addressId: string) => {
        try {
          trackJourneyEvent('platform', 'buttonClickEvent', {
            targetName: 'property-marker-click',
          });
        } catch {
          // Listing navigation must not depend on analytics availability.
        }
        openListing(addressId);
      },
      [openListing, trackJourneyEvent],
    );

    const focusListing = useCallback((addressId: string) => {
      setFocusedAddressId(addressId);
    }, []);

    const blurListing = useCallback(() => {
      setFocusedAddressId(null);
    }, []);

    const popupAddressId = focusedAddressId ?? hoveredAddressId;
    const popupProperty = popupAddressId ? propertiesByAddressId.get(popupAddressId) : undefined;
    const popupId = popupAddressId ? `cosmos-map-marker-popup-${popupAddressId}` : undefined;
    const hoveredAddressIsPresent = Boolean(
      hoveredAddressId && propertiesByAddressId.has(hoveredAddressId),
    );
    const focusedAddressIsPresent = Boolean(
      focusedAddressId && propertiesByAddressId.has(focusedAddressId),
    );
    const markerImageIds = useMemo(() => {
      const imageIds = new Set(
        featureCollection.features.map(({ properties }) => properties.imageId),
      );
      if (popupAddressId) {
        const activeFeature = featureCollection.features.find(
          ({ properties }) => properties.addressId === popupAddressId,
        );
        if (activeFeature) {
          imageIds.add(
            focusedAddressId
              ? activeFeature.properties.focusedImageId
              : activeFeature.properties.highlightedImageId,
          );
        }
      }
      return imageIds;
    }, [featureCollection, focusedAddressId, popupAddressId]);

    useEffect(() => {
      const hoverCandidateAddressId = hoverCandidateAddressIdRef.current;
      if (hoverCandidateAddressId && !propertiesByAddressId.has(hoverCandidateAddressId)) {
        clearHover();
      }
      if (hoveredAddressId && !hoveredAddressIsPresent) {
        // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
        clearHover();
      }
      if (focusedAddressId && !focusedAddressIsPresent) {
        setFocusedAddressId(null);
      }
    }, [
      clearHover,
      focusedAddressId,
      focusedAddressIsPresent,
      hoveredAddressId,
      hoveredAddressIsPresent,
      propertiesByAddressId,
    ]);

    useEffect(() => {
      if (hoveredAddressId && hoveredAddressIsPresent) {
        trackMarkerImpression(hoveredAddressId);
      }
    }, [hoveredAddressId, hoveredAddressIsPresent]);

    useEffect(() => {
      if (focusedAddressId && focusedAddressIsPresent) {
        trackMarkerImpression(focusedAddressId);
      }
    }, [focusedAddressId, focusedAddressIsPresent]);

    // react-map-gl applies layer filters during render, so the replacement image
    // must enter the atlas before the browser paints that layer update.
    useIsomorphicLayoutEffect(() => {
      if (!map) {
        return undefined;
      }
      let isActive = true;
      const registerImages = () => {
        if (!isActive || !map.isStyleLoaded()) {
          return;
        }
        registerMapMarkerImages({ imageIds: markerImageIds, map });
      };
      const registerMissingImage = ({ id }: { id: string }) => {
        if (markerImageIds.has(id)) {
          registerMapMarkerImage({ id, map });
        }
      };
      registerImages();
      map.on('styledata', registerImages);
      map.on('styleimagemissing', registerMissingImage);
      const removeUnusedImages = () => {
        if (!isActive) {
          return;
        }
        const registeredImageIds = getRegisteredMapMarkerImageIds(map);
        for (const imageId of registeredImageIds) {
          if (!markerImageIds.has(imageId) && map.hasImage(imageId)) {
            map.removeImage(imageId);
            registeredImageIds.delete(imageId);
          }
        }
      };
      map.once('idle', removeUnusedImages);
      return () => {
        isActive = false;
        map.off('styledata', registerImages);
        map.off('styleimagemissing', registerMissingImage);
        map.off('idle', removeUnusedImages);
      };
    }, [map, markerImageIds]);

    useEffect(() => {
      if (!map || !document.fonts || document.fonts.status === 'loaded') {
        return undefined;
      }
      let isActive = true;
      void document.fonts?.ready.then(() => {
        if (isActive) {
          refreshMapMarkerImages({
            imageIds: getRegisteredMapMarkerImageIds(map),
            map,
          });
        }
        return undefined;
      });
      return () => {
        isActive = false;
      };
    }, [map]);

    useEffect(() => {
      if (!map) {
        return undefined;
      }
      const handleClick = (event: MapMouseEvent) => {
        const addressId = marketplaceMarkerAddressIdAtPoint({ event, map });
        if (addressId && propertiesByAddressId.has(addressId)) {
          openCanvasListing(addressId);
        }
      };
      map.on('click', handleClick);
      return () => {
        map.off('click', handleClick);
      };
    }, [map, openCanvasListing, propertiesByAddressId]);

    useEffect(() => {
      if (!map || !isDesktop) {
        // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
        clearHover();
        return undefined;
      }
      const handleMouseMove = (event: MapMouseEvent) => {
        if (map.dragPan.isActive()) {
          clearHover();
          return;
        }
        const addressId = marketplaceMarkerAddressIdAtPoint({ event, map });
        const cursor = addressId ? 'pointer' : 'grab';
        const canvas = map.getCanvas();
        if (canvas.style.cursor !== cursor) {
          canvas.style.cursor = cursor;
        }
        if (addressId === hoverCandidateAddressIdRef.current) {
          return;
        }
        cancelHoverTimer();
        hoverCandidateAddressIdRef.current = addressId;
        setHoveredAddressId(null);
        if (addressId) {
          hoverTimerRef.current = window.setTimeout(() => {
            setHoveredAddressId(addressId);
            hoverTimerRef.current = null;
          }, HOVER_INTENT_MS);
        }
      };
      const handleMouseLeave = () => {
        clearHover();
        map.getCanvas().style.cursor = 'grab';
      };
      map.on('mousemove', handleMouseMove);
      map.on('mouseleave', handleMouseLeave);
      return () => {
        map.off('mousemove', handleMouseMove);
        map.off('mouseleave', handleMouseLeave);
        clearHover();
        map.getCanvas().style.cursor = 'grab';
      };
    }, [cancelHoverTimer, clearHover, isDesktop, map]);

    useEffect(() => {
      if (!focusedAddressId) {
        return undefined;
      }
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          setFocusedAddressId(null);
        }
      };
      window.addEventListener('keydown', handleEscape);
      return () => {
        window.removeEventListener('keydown', handleEscape);
      };
    }, [focusedAddressId]);

    const baseFilter = useMemo<FilterSpecification>(
      () => (popupAddressId ? ['!=', ['get', 'addressId'], popupAddressId] : ['has', 'addressId']),
      [popupAddressId],
    );
    const highlightedFilter = useMemo<FilterSpecification>(
      () =>
        popupAddressId
          ? ['==', ['get', 'addressId'], popupAddressId]
          : ['==', ['get', 'addressId'], ''],
      [popupAddressId],
    );
    const highlightedLayerLayout = useMemo<SymbolLayerSpecification['layout']>(
      () => ({
        ...marketplaceHighlightedMarkerLayer.layout,
        'icon-image': focusedAddressId ? ['get', 'focusedImageId'] : ['get', 'highlightedImageId'],
      }),
      [focusedAddressId],
    );

    return (
      <>
        <Source id={MARKETPLACE_MARKER_SOURCE_ID} type="geojson" data={featureCollection}>
          <Layer {...marketplaceMarkerLayer} filter={baseFilter} />
          <Layer
            {...marketplaceHighlightedMarkerLayer}
            filter={highlightedFilter}
            layout={highlightedLayerLayout}
          />
        </Source>
        {featureCollection.features.map((feature) => {
          const property = propertiesByAddressId.get(feature.properties.addressId);
          if (!property) {
            return null;
          }
          const isFocused = focusedAddressId === property.addressId;
          return (
            <AccessibleMapMarker
              key={property.addressId}
              isDesktop={isDesktop}
              isFocused={isFocused}
              onBlur={blurListing}
              onFocus={focusListing}
              onListingClick={openListing}
              popupId={isFocused ? popupId : undefined}
              property={property}
            />
          );
        })}
        {isDesktop && map && popupProperty && popupId ? (
          <MapMarkerPopup
            key={popupProperty.addressId}
            map={map}
            popupId={popupId}
            property={popupProperty}
          />
        ) : null}
      </>
    );
  },
);

MarketplaceMapMarkers.displayName = 'MarketplaceMapMarkers';
